import ArrowsSortIcon, { SORT_STATE } from './ArrowsSortIcon';
import { COLUMN_TYPE } from './scanTableColumns';
import {
  formatChangeValue,
  formatNumber,
  getChangeClass
} from './scanTableFormatters';

export const HeaderCell = ({ column, sortState, onSort }) => {
  const ariaSort =
    sortState === SORT_STATE.ASC
      ? 'ascending'
      : sortState === SORT_STATE.DESC
      ? 'descending'
      : 'none';

  return (
    <div
      className={`header-cell${
        column.width ? ` header-cell--w${column.width}` : ''
      }`}
      role="columnheader"
      scope="col"
      aria-sort={ariaSort}
    >
      <div className="header-content">
        <span className="header-text">{column.header}</span>
        <ArrowsSortIcon
          sortState={sortState}
          onClick={onSort}
          ariaLabel={`Sort ${column.header}`}
        />
      </div>
    </div>
  );
};

export const PriceCell = ({ value }) => {
  const display = value == null || value === '' ? '-' : formatNumber(value);
  return (
    <div className="body-cell body-cell--price" role="cell">
      <span className="cell-content cell-content--price">{display}</span>
    </div>
  );
};

export const ChangeCell = ({ value }) => {
  if (value != null && typeof value === 'object') {
    const abs = value.absolute ?? value.abs ?? null;
    const pct = value.percent ?? value.pct ?? value.changePercent ?? null;
    const changeClass = getChangeClass(abs ?? pct);
    return (
      <div className="body-cell body-cell--change" role="cell">
        <div className="cell-multi">
          <span className="cell-value cell-value--absolute">
            {abs != null ? formatChangeValue(abs) : '-'}
          </span>
          <span className={`cell-value cell-value--percent ${changeClass}`}>
            {pct != null ? `${formatChangeValue(pct)}%` : '-'}
          </span>
        </div>
      </div>
    );
  }

  const changeClass = getChangeClass(value);
  const display =
    value == null || value === '' ? '-' : formatChangeValue(value);
  return (
    <div className="body-cell body-cell--change" role="cell">
      <span className={`cell-content cell-content--change ${changeClass}`}>
        {display}
      </span>
    </div>
  );
};

export const TextCell = ({ value }) => {
  const display = value == null || value === '' ? '-' : String(value);
  return (
    <div className="body-cell body-cell--text" role="cell">
      <span className="cell-content cell-content--text">{display}</span>
    </div>
  );
};

export const DefaultCell = ({ value, width }) => {
  let display;
  if (value == null || value === '') {
    display = '-';
  } else if (typeof value === 'object') {
    display =
      Object.keys(value).length === 0 ? '-' : Object.values(value).join(', ');
  } else if (typeof value === 'number') {
    display = formatNumber(value);
  } else {
    display = String(value);
  }

  return (
    <div
      className={`body-cell${width ? ` body-cell--w${width}` : ''}`}
      role="cell"
    >
      <span className="cell-content">{display}</span>
    </div>
  );
};

export const SymbolCell = ({
  result,
  symbolKey,
  metadata,
  localize,
  onSymbolClick
}) => {
  const symbolValue = result[symbolKey];
  const meta = metadata[symbolValue] || {};
  const displayName = meta.jpShortName || meta.name || symbolValue;

  const detailParts = [
    meta.root,
    meta.exchangeName,
    meta.jpIndustry
      ? localize(`symbol.industry.${meta.jpIndustry}`) || meta.jpIndustry
      : null
  ].filter(Boolean);

  return (
    <div className="body-cell-fixed" role="cell">
      <div className="scan-symbol-content">
        <button
          type="button"
          className="scan-symbol-name"
          onClick={(e) => {
            e.stopPropagation();
            onSymbolClick && onSymbolClick(symbolValue);
          }}
          aria-label={`${
            localize('scan.label.symbol') || '銘柄'
          }: ${symbolValue}`}
        >
          <span className="scan-symbol-text">{displayName}</span>
        </button>
        <div className="scan-symbol-details">{detailParts.join(' ')}</div>
      </div>
    </div>
  );
};

/**
 * Renders the appropriate body cell based on column type.
 */
export const BodyCell = ({ column, result }) => {
  const raw = result[column.key];

  switch (column.type) {
    case COLUMN_TYPE.PRICE:
      return <PriceCell value={raw} />;
    case COLUMN_TYPE.CHANGE:
      return <ChangeCell value={raw} />;
    case COLUMN_TYPE.TEXT:
      return <TextCell value={raw} />;
    default:
      return <DefaultCell value={raw} width={column.width} />;
  }
};
